import { useRef, useState, useEffect } from 'react';
import { Box, HStack, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text } from '@chakra-ui/react';

// Small inline icons so we don't depend on a specific icon set being installed.
const PlayIcon = () => (
  <Box as="svg" viewBox="0 0 24 24" w="18px" h="18px" fill="currentColor"><path d="M8 5v14l11-7z" /></Box>
);
const PauseIcon = () => (
  <Box as="svg" viewBox="0 0 24 24" w="18px" h="18px" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></Box>
);
const VolumeIcon = () => (
  <Box as="svg" viewBox="0 0 24 24" w="16px" h="16px" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" /></Box>
);

const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Themed "listen to this article" pill bar. Plays a pre-generated, cached MP3
 * (no API cost). Renders nothing when the article has no audio.
 */
const ArticleAudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    // Reset when the source changes (e.g. navigating between articles).
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
  }, [src]);

  if (!src) return null;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // play() returns a promise that can reject (e.g. autoplay policy); only
      // flip to "playing" once it actually starts so the icon stays in sync.
      const started = audio.play();
      if (started && typeof started.then === 'function') {
        started.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else {
        setIsPlaying(true);
      }
    }
  };

  const handleSeek = (value) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = (value / 100) * duration;
    setProgress(value);
  };

  const handleVolume = (value) => {
    const audio = audioRef.current;
    const v = value / 100;
    if (audio) audio.volume = v;
    setVolume(v);
  };

  return (
    <Box
      w="100%"
      bg="gray.900"
      border="1px solid"
      borderColor="gray.700"
      borderRadius="full"
      px={4}
      py={2}
    >
      <HStack spacing={4}>
        <IconButton
          aria-label={isPlaying ? 'Pause narration' : 'Play narration'}
          onClick={togglePlay}
          icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
          isRound
          size="sm"
          bg="green.500"
          color="white"
          _hover={{ bg: 'green.400' }}
          flexShrink={0}
        />

        <Text fontSize="xs" color="green.500" textTransform="uppercase" letterSpacing="wide" flexShrink={0}>
          Listen
        </Text>

        <Slider
          aria-label="Seek"
          value={progress}
          onChange={handleSeek}
          focusThumbOnChange={false}
          flex="1"
        >
          <SliderTrack bg="gray.700">
            <SliderFilledTrack bg="green.500" />
          </SliderTrack>
          <SliderThumb boxSize={3} />
        </Slider>

        <Text fontSize="xs" color="gray.400" flexShrink={0} minW="80px" textAlign="right">
          {formatTime((progress / 100) * duration)} / {formatTime(duration)}
        </Text>

        <HStack spacing={1} w="90px" flexShrink={0} display={{ base: 'none', md: 'flex' }}>
          <Box color="gray.400"><VolumeIcon /></Box>
          <Slider aria-label="Volume" value={volume * 100} onChange={handleVolume} focusThumbOnChange={false}>
            <SliderTrack bg="gray.700">
              <SliderFilledTrack bg="green.500" />
            </SliderTrack>
            <SliderThumb boxSize={3} />
          </Slider>
        </HStack>
      </HStack>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          if (e.target.duration) setProgress((e.target.currentTime / e.target.duration) * 100);
        }}
        onEnded={(e) => {
          setIsPlaying(false);
          setProgress(0);
          e.target.currentTime = 0; // rewind so the next Play starts from the top
        }}
      />
    </Box>
  );
};

export default ArticleAudioPlayer;
